/** Task creation form. Shows a type selector, then renders the type-specific form. */

import { CheckCircle } from 'lucide-react'
import { type ComponentType, useState } from 'react'
import { toast } from 'sonner'

import { CREATABLE_TASK_TYPES, type CreatableTaskType } from '@snootsvet/shared'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'

import { FollowUpTaskForm } from './follow-up-task.form'
import { LabsTaskForm } from './labs-task.form'
import {
  MedicationRequestTaskForm,
  OtherTaskForm,
  PrescriptionRequestTaskForm,
  ReferralTaskForm,
} from './other-task.form'

interface TaskFormProps {
  defaultValues?: { patientId?: string }
  onSuccess?: () => void
  onCancel?: () => void
}

const FORM_MAP: Record<CreatableTaskType, ComponentType<TaskFormProps>> = {
  follow_up: FollowUpTaskForm,
  labs: LabsTaskForm,
  medication_request: MedicationRequestTaskForm,
  prescription_request: PrescriptionRequestTaskForm,
  referral: ReferralTaskForm,
  other: OtherTaskForm,
}

export function TaskForm({ defaultValues, onSuccess, onCancel }: TaskFormProps) {
  const [taskType, setTaskType] = useState<CreatableTaskType>('follow_up')

  const FormComponent = FORM_MAP[taskType]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <span className="font-medium">Type</span>
        <Select
          items={CREATABLE_TASK_TYPES}
          value={taskType}
          onValueChange={(v) => {
            if (v) setTaskType(v)
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a type" />
          </SelectTrigger>
          <SelectContent>
            {CREATABLE_TASK_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <FormComponent
        key={taskType}
        defaultValues={defaultValues}
        onSuccess={() => {
          toast('Task created', { icon: <CheckCircle className="size-4 text-success-fg" /> })
          onSuccess?.()
        }}
        onCancel={onCancel}
      />
    </div>
  )
}
